import React, { useEffect, useState } from "react";
import useThemeStore from "../store/themeStore";
import usePwaInstallStore from "../store/pwaInstallStore";
import {
  isIOS,
  isIOSChrome,
  isStandalone,
  isMobile,
  getPwaIconUrl,
  BeforeInstallPromptEvent,
} from "../utils/pwaUtils";
import {
  isPromptSuppressed,
  hasAutoOpenedThisSession,
  markAutoOpened,
} from "../utils/pwaPromptState";
import { visibleAccent, readableTextColour } from "../utils/colourContrast";
import pwaGuideGif from "../images/pwa/pwa.gif";

const OVERLAY_Z_INDEX = 10000;

// Centralised "save this site as an app" modal. This is the single PWA install
// surface: it captures beforeinstallprompt into the shared store, auto-opens once
// per session on eligible mobile landings, and is re-openable from the header
// "Install App" menu entry. Reuses the centred-overlay shell from TermsModal.
const PwaInstallModal: React.FC = () => {
  const theme = useThemeStore((state) => state.theme);
  const isModalOpen = usePwaInstallStore((state) => state.isModalOpen);
  const deferredPrompt = usePwaInstallStore((state) => state.deferredPrompt);
  const setDeferredPrompt = usePwaInstallStore((state) => state.setDeferredPrompt);
  const openModal = usePwaInstallStore((state) => state.openModal);
  const closeModal = usePwaInstallStore((state) => state.closeModal);
  const triggerInstall = usePwaInstallStore((state) => state.triggerInstall);

  const [showGuide, setShowGuide] = useState(false);

  // Eligibility controller. Always captures the native prompt (so the menu entry
  // can install even during a cooldown); only auto-opens the modal when it isn't
  // suppressed and hasn't already opened this session.
  useEffect(() => {
    if (!theme?.pwaEnabled || isStandalone() || !isMobile()) return;

    const maybeAutoOpen = () => {
      if (isPromptSuppressed() || hasAutoOpenedThisSession()) return;
      openModal();
      markAutoOpened();
    };

    // iOS never fires beforeinstallprompt — there's nothing to capture.
    if (isIOS()) {
      maybeAutoOpen();
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent); // always capture
      maybeAutoOpen();
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme?.pwaEnabled]);

  // The modal stays mounted (it only renders null when closed), so reset the
  // nested guide lightbox whenever the modal closes — otherwise a close while the
  // guide was open would resurrect it on the next open.
  useEffect(() => {
    if (!isModalOpen) setShowGuide(false);
  }, [isModalOpen]);

  if (!isModalOpen || !theme) return null;

  const color1 = theme.color1 || "#1e1b4b";
  const ios = isIOS();
  const iosChrome = isIOSChrome();
  // App (home-screen) icon, not the wider website logo.
  const iconUrl = getPwaIconUrl(theme);
  // The modal sits on a white card, so a white/near-white color1 would make the
  // accent (install button fill, iOS button border/text) invisible — fall back
  // to color2, then a dark default.
  const accent = visibleAccent(color1, theme.color2);
  const installBtnTextColour = readableTextColour(
    accent,
    theme.buttonTxtColour,
    theme.txtColor || "#ffffff"
  );

  // Drive the native prompt; the store clears the spent prompt and takes down the
  // modal from the outcome. The button only renders when a prompt exists.
  const handleInstall = () => {
    triggerInstall();
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: OVERLAY_Z_INDEX,
        padding: "16px",
      }}
      onClick={closeModal}
    >
      <div
        style={{
          position: "relative",
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          textAlign: "center",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={closeModal}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "32px",
            height: "32px",
            border: "none",
            background: "transparent",
            color: "#999",
            fontSize: "22px",
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          &times;
        </button>

        {iconUrl && (
          <img
            src={iconUrl}
            alt={theme.name}
            style={{
              width: "56px",
              height: "56px",
              objectFit: "contain",
              borderRadius: "10px",
              margin: "0 auto 12px",
            }}
          />
        )}

        <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", fontWeight: 600, color: "#222" }}>
          Get the {theme.name} app
        </h3>
        <p style={{ margin: "0 0 20px 0", fontSize: "14px", color: "#666", lineHeight: 1.5 }}>
          If you want to save as an application on your phone please click below.
        </p>

        {ios ? (
          <IosInstructions
            isIosChrome={iosChrome}
            accentColour={accent}
            onShowGuide={() => setShowGuide(true)}
          />
        ) : deferredPrompt ? (
          <button
            type="button"
            onClick={handleInstall}
            style={{
              width: "100%",
              padding: "14px 16px",
              border: "none",
              borderRadius: "8px",
              background: accent,
              color: installBtnTextColour,
              fontSize: "16px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Add to your home screen
          </button>
        ) : (
          // Android with no captured prompt (already used, or it never fired):
          // the browser menu is always available, so guide the user there rather
          // than showing a dead button.
          <p style={{ margin: 0, fontSize: "14px", color: "#444", lineHeight: 1.5 }}>
            To install, open your browser menu (<strong>⋮</strong>) and choose{" "}
            <strong>“Install app”</strong> or <strong>“Add to Home screen”</strong>.
          </p>
        )}

        {showGuide && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: OVERLAY_Z_INDEX + 1,
              padding: "16px",
            }}
            onClick={() => setShowGuide(false)}
          >
            <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                aria-label="Close guide"
                onClick={() => setShowGuide(false)}
                style={{
                  position: "absolute",
                  top: "-40px",
                  right: 0,
                  border: "none",
                  background: "transparent",
                  color: "#fff",
                  fontSize: "28px",
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                &times;
              </button>
              <img
                src={pwaGuideGif}
                alt="How to add this site to your iPhone home screen"
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  borderRadius: "12px",
                  display: "block",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// iOS Safari cannot trigger an install programmatically, so we explain the manual
// steps and offer the animated guide. The key point users miss is that the menu
// needs a press-and-hold, not a single tap. Centred to match the rest of the modal.
const IosInstructions: React.FC<{
  isIosChrome: boolean;
  accentColour: string;
  onShowGuide: () => void;
}> = ({ isIosChrome, accentColour, onShowGuide }) => {
  if (isIosChrome) {
    return (
      <p style={{ margin: 0, fontSize: "14px", color: "#444", lineHeight: 1.5 }}>
        Open this site in <strong>Safari</strong> to add it to your home screen — iOS only
        allows installing from Safari.
      </p>
    );
  }

  return (
    <div style={{ textAlign: "center" }}>
      <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#444", lineHeight: 1.6 }}>
        <strong>1.</strong> <strong>Press and hold</strong> the menu button (the{" "}
        <strong>•••</strong> or share icon) at the bottom of Safari — a single tap may not
        open it.
      </p>
      <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#444", lineHeight: 1.6 }}>
        <strong>2.</strong> Choose <strong>“Add to Home Screen”</strong> from the menu.
      </p>
      <button
        type="button"
        onClick={onShowGuide}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "10px 16px",
          border: `1px solid ${accentColour}`,
          borderRadius: "8px",
          background: "transparent",
          color: accentColour,
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            border: `1.5px solid ${accentColour}`,
            fontSize: "13px",
            fontStyle: "italic",
            fontWeight: 700,
          }}
        >
          i
        </span>
        Show me how
      </button>
    </div>
  );
};

export default PwaInstallModal;
